// Copyright (c) Move Closer 2022.

import { Boundaries } from './boundaries'
import { Degree, Point } from './point'
import { UserAddress, UserLocation } from './contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
const errorCodes: Record<number, string> = {
  0: 'NOT_SUPPORTED',
  1: 'PERMISSION_DENIED',
  2: 'POSITION_UNAVAILABLE',
  3: 'TIMEOUT'
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export const TILE_SIZE_256 = 256
export const TILE_SIZE_512 = 512
const ZOOM_MAX = 15

/**
 * Helper function for calculating Representative Fraction.
 * The latitude is converted to radians and mapped to the range of [-PI / 2, PI / 2]
 * @see https://en.wikipedia.org/wiki/Scale_(map)#The_equirectangular_projection
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
function latRad (lat: number): number {
  const sin = Math.sin((lat * Math.PI) / 180)
  const radX2 = Math.log((1 + sin) / (1 - sin)) / 2

  return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2
}

/**
 * Calculate optimal zoom relative to map canvas.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
function zoom (mapPx: number, worldPx: number, fr: number): number {
  return Math.floor(Math.log(mapPx / worldPx / fr) / Math.LN2)
}

/**
 * Calculates minimum and maximum for both latitude and longitude of provided Points.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export function calculateBoundaries (points: Point[]): Boundaries {
  if (points.length === 0) {
    return new Boundaries(-1, -1, -1, -1)
  }

  if (points.length === 1) {
    return new Boundaries(points[0].lat, points[0].lat, points[0].lng, points[0].lng)
  }

  let latMin: number = -1
  let latMax: number = -1
  let lngMin: number = -1
  let lngMax: number = -1

  let lat, lng
  points.forEach((point) => {
    lat = point.lat
    lng = point.lng

    if (latMin === -1 || lat < latMin) {
      latMin = lat
    }

    if (latMax === -1 || lat > latMax) {
      latMax = lat
    }

    if (lngMin === -1 || lng < lngMin) {
      lngMin = lng
    }

    if (lngMax === -1 || lng > lngMax) {
      lngMax = lng
    }
  })

  return new Boundaries(latMin, latMax, lngMin, lngMax)
}

/**
 * Calculates distance between given Points.
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export function calculateDistance (pointA: Point, pointB: Point): number {
  const R = 6371
  const latA = pointA.x.toRadians()
  const latB = pointB.x.toRadians()
  const deltaLat = new Degree(pointB.lat - pointA.lat).toRadians()
  const deltaLng = new Degree(pointB.lng - pointA.lng).toRadians()

  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(latA) * Math.cos(latB) *
    Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculate zoom for both latitude and longitude.
 * @see https://en.wikipedia.org/wiki/Mercator_projection
 *
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export function calculateZoom (bounds: Boundaries, mapWidth: number, mapHeight: number, tileSize: number): number {
  const ne = bounds.ne
  const sw = bounds.sw

  const latFr = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI

  const lngDiff = ne.lng - sw.lng
  const lngFr = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

  const latZoom = zoom(mapHeight, tileSize, latFr)
  const lngZoom = zoom(mapWidth, tileSize, lngFr)

  return Math.min(latZoom, lngZoom, ZOOM_MAX)
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export const defaultCoordinates = {
  lat: 52.0947634,
  lng: 19.3880948,
  zoom: 6
}

/**
 * Attempts to acquire user's geolocation.
 */
export async function detectUserGeolocation (): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      // eslint-disable-next-line prefer-promise-reject-errors
      reject({
        type: errorCodes[0],
        message: 'Geolocation is not supported in user\'s browser'
      })

      return
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        resolve(pos)
      },
      err => {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          type: errorCodes[err.code],
          message: err.message
        })
      }
    )
  })
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export async function locateBasedOnPostCode (postCode: string): Promise<UserLocation | UserLocation[]> {
  const url = new URL('/search', 'https://nominatim.openstreetmap.org')

  url.searchParams.append('format', 'json')
  url.searchParams.append('addressdetails', '1')
  url.searchParams.append('postalcode', postCode)

  return await fetch(url.toString()).then(res => res.json())
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export async function locateBasedOnQuery (query: string): Promise<UserLocation | UserLocation[]> {
  const url = new URL('/search', 'https://nominatim.openstreetmap.org')

  url.searchParams.append('format', 'json')
  url.searchParams.append('addressdetails', '1')
  url.searchParams.append('q', query)

  return await fetch(url.toString()).then(res => res.json())
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export async function locateUser (address: UserAddress): Promise<UserLocation> {
  const url = new URL('/search', 'https://nominatim.openstreetmap.org')

  if (address.country) {
    url.searchParams.append('country', address.country)
  }
  url.searchParams.append('city', address.city)
  if (address.postcode) {
    url.searchParams.append('postcode', address.postcode)
  }
  url.searchParams.append('street', address.street)
  url.searchParams.append('format', 'json')
  url.searchParams.append('addressdetails', '1')

  let response = await fetch(url.toString()).then(res => res.json())
  if (response.length === 0) {
    url.searchParams.delete('street')
    response = await fetch(url.toString()).then(res => res.json())
  }

  const location = response[0] || { address: {} }

  return {
    county: location.address.county || null,
    city: location.address.city || location.address.village || null,
    postcode: location.address.postcode || null,
    pos: new Point(parseFloat(location.lat), parseFloat(location.lon))
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (edited)
 */
export function toQueryStreetAddress (streetAddress: string[]): string {
  const street = streetAddress.join(' ')
    .replace(',', '')
    .replace('ul. ', '')
    .replace('Ul.', '')
    .replace('Al. ', '')
    .replace('al. ', '')
    .replace('Al.', '')
    .replace('al.', '')
    .replace('.', '')

  return street.slice(0, street.lastIndexOf('/'))
}
